import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { useAuth0 } from "@auth0/auth0-react";
import { infiniteNestLink, Link, Route, Switch } from "polyrouter";
import { Layout } from "views/Main/Layouts";
import ConsolidateAccounts from "./Consolidate/ConsolidateAccounts";
import { ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import {
  getColumns,
  HEADER_ID,
  itemCheckedWithSet,
  TableBody,
  TableBulkItemHeader,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import {
  Grant,
  Role,
  Tenant,
  TenantType,
  useCountAccountsQuery,
  useAccountsListQuery,
  useTenantUserPermisionsQuery,
  TenantSort,
  TenantSortDirection,
  AccountsListQuery,
  TenantUserPermisionsQuery
} from "generated/graphql";
import { AccountRow } from "./List/AccountRow";
import { State } from "store";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { generateTableHeaders } from "utilities/tableUtils";
import { Loader, OptionType, useFeatureFlagWithTenant } from "elements";
import { accountLimit } from "Constants";
import DeleteAccountModal from "./Delete/DeleteAccountModal";
import AddAccountModal from "./Add/AddAccountModal";
import { useTenantsControlState } from "./TenantsControlState";
import { usePermissions } from "rbac";
import { TableFilter } from "collections/Table";
import { MemberRoleNames } from "roles";
import { SortState } from "store/filters";

export type AccountForTable = Pick<
  Tenant,
  "id" | "name" | "type" | "deviceCount" | "memberCount" | "__typename"
> & {
  role?: LocalKeys;
  permissions?: string[];
  sites: {
    count: number;
  };
  rooms?: {
    count?: number;
  } | null;
};

export type AccountTableFields =
  | "name"
  | "sites"
  | "rooms"
  | "devices"
  | "members"
  | "role"
  | "type";

function adaptSort(uiSort: SortState): TenantSort {
  const direction =
    uiSort.direction === "ASC"
      ? TenantSortDirection.Asc
      : TenantSortDirection.Desc;
  return {
    fields: [
      { name: "name", direction: direction },
      { name: "id", direction: direction }
    ]
  };
}

function findRole(grant: Grant): Role | undefined {
  const supportedRoles = [...MemberRoleNames, "device_user"];
  return grant.roles.find(role => supportedRoles.includes(role.name));
}

function getQueriedAccounts(
  tenantsListData: AccountsListQuery | undefined,
  permissionsData: TenantUserPermisionsQuery | undefined,
  blackList: string[]
): AccountForTable[] {
  if (tenantsListData && permissionsData) {
    const filteredData = tenantsListData.tenants.filter(
      tenant => !blackList.includes(tenant.id)
    );
    let queriedAccounts: AccountForTable[] = filteredData as AccountForTable[];
    const grants: Grant[] = (permissionsData?.accessor?.grants ??
      []) as Grant[];
    if (grants.length > 0) {
      queriedAccounts = queriedAccounts.map(account => {
        const grant = grants.find(grant => grant.resourceId === account.id);
        if (grant) {
          const permissions = findRole(grant)?.permissions.map(p => p.name);
          const role = findRole(grant)?.name as LocalKeys;
          return {
            ...account,
            permissions,
            role
          };
        }
        return account;
      });
    }
    return queriedAccounts;
  }
  return [];
}

export const AccountsView: React.FC = () => {
  const { user } = useAuth0();
  const { t } = useTranslation();
  const [
    {
      sort,
      from,
      accountsChanged,
      selected: allSelectedIDs,
      previousNamePrefix,
      namePrefix,
      accountTypeFilter,
      blackList
    },
    actions
  ] = useTenantsControlState();
  const tenantSort = adaptSort(sort);
  const {
    data: tenantsListData,
    fetching: tenantsListFetching,
    stale: tenantsListStale,
    executor: refetchTenantsList
  } = useAccountsListQuery({
    variables: {
      namePrefix: previousNamePrefix,
      from,
      limit: accountLimit,
      tenantType: accountTypeFilter || undefined,
      sort: tenantSort
    },
    requestPolicy: "cache-first"
  });

  const {
    data: permissionsData,
    fetching: permissionsFetching,
    executor: refetchPermissions
  } = useTenantUserPermisionsQuery({
    variables: {
      userId: user ? user.sub : "",
      tags: []
    },
    requestPolicy: "cache-first"
  });
  const queriedAccounts = getQueriedAccounts(
    tenantsListData,
    permissionsData,
    blackList
  );

  const selectedIDs = allSelectedIDs.filter(id => id !== HEADER_ID);
  const selected: AccountForTable[] = queriedAccounts.filter(a =>
    selectedIDs.includes(a.id)
  );

  const { data: countData } = useCountAccountsQuery();
  const hasPermission = usePermissions();
  const isPolyAdmin = hasPermission("READ_TENANT", "*");

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.ACCOUNTS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  useEffect(() => {
    if (namePrefix !== previousNamePrefix)
      actions.updatePreviousNamePrefix(namePrefix);
  }, [actions, namePrefix, previousNamePrefix]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // After account delete reset cursor if needed, then re-execute query
    if (accountsChanged) {
      refetchTenantsList({ requestPolicy: "network-only" });
      refetchPermissions({ requestPolicy: "network-only" });
      actions.updateAccountsChanged(false);
    }
  }, [accountsChanged, actions, refetchTenantsList, refetchPermissions, from]);

  const [isConsolidationEnabled] = useFeatureFlagWithTenant(
    "ACCOUNT_CONSOLIDATION"
  );

  const count = countData?.tenantCount ?? 0;

  const toggleItem = toggleItemWithSet(
    count,
    allSelectedIDs,
    actions.updateSelected
  );
  const itemChecked = itemCheckedWithSet(allSelectedIDs);
  const isDeleteAllowed =
    selectedIDs.length > 0 &&
    selectedIDs.every(id => hasPermission("UPDATE_TENANT", id));

  const columnTranslations = {
    [COLUMNS.NAME]: t("ACCOUNT_NAME"),
    [COLUMNS.SITES]: t("SITES"),
    [COLUMNS.ROOMS]: t("ROOMS"),
    [COLUMNS.DEVICES]: t("DEVICES"),
    [COLUMNS.MEMBERS]: t("ACCOUNT_MEMBERS"),
    [COLUMNS.TENANT_TYPE]: t("TENANT_TYPE"),
    [COLUMNS.ROLE]: t("YOUR_ROLE")
  };

  const getTenantTypes = function(): Array<OptionType> {
    return [
      {
        value: TenantType.Enterprise,
        text: "Enterprise"
      },
      {
        value: TenantType.ServiceProvider,
        text: "Service Provider"
      },
      {
        value: TenantType.Distributor,
        text: "Distributor"
      }
    ];
  };

  const renderAccountTypeHeader = function() {
    const options = getTenantTypes();
    return (
      <th className="table-header">
        <TableFilter
          fieldName="AccountType"
          title="Account Type"
          filterValue={accountTypeFilter}
          filterOptions={options}
          onFilter={(field, value) => {
            actions.setAccountTypeFilter(value as TenantType);
          }}
        />
      </th>
    );
  };

  const getTableHeaders = (column: string, title: string) => {
    const testId = column === COLUMNS.NAME ? "account-name-header" : undefined;

    if (column === COLUMNS.TENANT_TYPE) {
      return renderAccountTypeHeader();
    }

    const sortable = column === COLUMNS.NAME;

    return (
      <TableHeaderItem
        fieldName={column}
        title={title}
        sort={
          sortable
            ? {
                currentSort: sort,
                onSort: actions.updateSort
              }
            : undefined
        }
        dataTestId={testId}
        key={column}
      />
    );
  };

  // Only poly admins should be shown the tenant type column.
  const finalColumns = isPolyAdmin
    ? shownColumns.filter(name => name !== "role")
    : shownColumns.filter(name => name !== "type");

  const { Content } = Layout;

  const incrementCallback = useCallback(
    () => actions.incrementFrom(accountLimit),
    [actions]
  );

  const hasNextPage = queriedAccounts.length < count;

  const shown = getColumns(finalColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  return (
    <div className="account-table-content">
      <Layout>
        <Content className="no-sidebar">
          <div className="table-heading">
            <span className="metric">{count}</span>
            <span className="title ml-5">{t("ACCOUNTS")}</span>
          </div>
          <div className="table-actions mb-15">
            <Link
              data-testid="add-account-button"
              nest
              to={"/add"}
              className="btn btn-primary"
            >
              {t("ADD_NEW")}
            </Link>
            {isConsolidationEnabled && (
              <ProtectedLink
                data-testid="consolidate"
                nest
                to="/consolidate"
                className="btn btn-secondary"
                requirement="MERGE_TENANT"
              >
                {t("CONSOLIDATE")}
              </ProtectedLink>
            )}
            <Link
              data-testid="delete-account-button"
              to={
                isDeleteAllowed
                  ? infiniteNestLink("/delete")
                  : infiniteNestLink("")
              }
              className={isDeleteAllowed ? "btn" : "btn disabled"}
            >
              {t("DELETE")}
            </Link>
            <TableSearchEdit
              search={{
                onSearch: actions.updateNamePrefix,
                searchPlaceholder: t("SEARCH_ACCOUNT_NAME")
              }}
              edit={{
                tableName: TABLE_NAME,
                shownColumns: shown,
                hiddenColumns: hidden
              }}
            />
          </div>
          <div className="table-container">
            <table className="table custom-table" data-testid="accounts-table">
              <TableBulkItemHeader
                showChecks={true}
                onCheck={() =>
                  toggleAll(
                    queriedAccounts
                      .map(account => account.id)
                      .filter(id => hasPermission("UPDATE_TENANT", id)),
                    allSelectedIDs,
                    actions.updateSelected
                  )
                }
                checked={itemChecked(HEADER_ID)}
              >
                {generateTableHeaders(shown, getTableHeaders)}
              </TableBulkItemHeader>
              <TableBody
                id={"accounts-table"}
                loading={tenantsListFetching}
                cols={shown.length}
                accommodateScrollbar
              >
                {queriedAccounts.map((account: AccountForTable, i: number) => (
                  <AccountRow
                    account={account}
                    shownColumns={shown.map(column => column.key)}
                    index={i}
                    listLength={queriedAccounts.length}
                    incrementFrom={incrementCallback}
                    count={count}
                    key={i}
                    onCheck={() => toggleItem(account.id)}
                    checked={itemChecked(account.id)}
                    fetching={tenantsListFetching || permissionsFetching}
                    hasPermission={hasPermission}
                    hasNextPage={hasNextPage}
                  />
                ))}
                <tr>
                  <td>
                    <Loader
                      loading={tenantsListFetching || tenantsListStale}
                      className="inline"
                    />
                  </td>
                </tr>
              </TableBody>
            </table>
          </div>
        </Content>
        <Switch>
          {isConsolidationEnabled && (
            <ProtectedRoute
              nest
              path="/consolidate"
              requirement="MERGE_TENANT"
              render={() => <ConsolidateAccounts accounts={queriedAccounts} />}
            />
          )}
          <Route
            nest
            path="/add"
            render={() => (
              <AddAccountModal
                onComplete={() => actions.updateAccountsChanged(true)}
              />
            )}
          />
          {isDeleteAllowed && (
            <Route
              nest
              path="/delete"
              render={() => (
                <DeleteAccountModal
                  accounts={selected}
                  onComplete={() => actions.updateAccountsChanged(true)}
                  addToBlackList={actions.addToBlackList}
                />
              )}
            />
          )}
        </Switch>
      </Layout>
    </div>
  );
};

export default AccountsView;
